import { useEffect, useState } from 'react';
import { Alert, Box, Chip, Paper, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { apiService, LikelyRange, LikelyRangeReliability } from '../services/api';
import PositionSizer from './PositionSizer';

// The horizons the range was tested at (Feature 43, range_calibration.py), in trading days
const TESTED_LABEL: Record<number, string> = {
  5: '1 week', 10: '2 weeks', 21: '1 month', 42: '2 months', 63: '3 months', 126: '6 months', 252: '1 year',
};
const label = (d: number) => TESTED_LABEL[d] ?? `${d} trading days`;
const pct0 = (v?: number | null) => (v == null ? '–' : `${Math.round(v * 100)}%`);
const signed = (v: number) => `${v > 0 ? '+' : ''}${(v * 100).toFixed(1)}%`;
const money = (v: number) => `$${v.toFixed(2)}`;
const GROUP_TEXT: Record<string, string> = {
  'S&P 500 stocks': 'S&P 500 stocks',
  'All 23 ETFs': 'ETFs',
  Bonds: 'bond ETFs',
  Commodities: 'commodity ETFs',
  'Stock indexes': 'stock-index ETFs',
  Other: 'property, dollar, energy and utilities ETFs',
};
const groupText = (g: string) => GROUP_TEXT[g] ?? g;
const METHOD_TEXT = { chronos: "Chronos-2's forecast", vol: "the past year's volatility" };
// Below this the range clearly failed in testing at that length
const FAILED = 0.7;
// Benjamini-Hochberg q under which one range was clearly better than the other
const CLEAR = 0.05;

export function LikelyRangeCard({ r, current }: { r: LikelyRange; current: number }) {
  const t = r.tested;
  if (r.lower == null || r.upper == null) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Likely range:</strong> not tested beyond 1 year. Chronos-2's own 80% range is{' '}
        {money(r.raw_lower)} – {money(r.raw_upper)}, but at every tested length that range held fewer than 80% of
        real prices, so expect it to be too narrow.
      </Alert>
    );
  }
  const other = r.method === 'chronos' ? r.vol : r.chronos;
  const otherName = r.method === 'chronos' ? 'The volatility range' : "Chronos-2's corrected range";
  // How many test dates the range shown won, over all lengths and at this one
  const groupWon = t?.group_dates != null && t.group_chronos_better_dates != null
    ? (r.method === 'chronos' ? t.group_chronos_better_dates : t.group_dates - t.group_chronos_better_dates) : null;
  const hereWon = t?.comparison_dates != null && t.chronos_better_dates != null
    ? (r.method === 'chronos' ? t.chronos_better_dates : t.comparison_dates - t.chronos_better_dates) : null;
  const clear = t?.q != null && t.q < CLEAR && hereWon != null && t.comparison_dates != null && hereWon > t.comparison_dates / 2;
  const halves = t?.halves ? Object.entries(t.halves) : [];
  return (
    <Paper sx={{ p: 2, mb: 2, border: 2, borderColor: 'success.main' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="overline" color="success.main" fontWeight={700}>
          Likely price range · {label(r.horizon)}
        </Typography>
        <Chip size="small" variant="outlined" label={`Built from ${METHOD_TEXT[r.method]}`} />
      </Box>
      <Typography variant="h4" fontWeight={700}>
        {money(r.lower)} – {money(r.upper)}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {signed(r.lower / current - 1)} to {signed(r.upper / current - 1)} from today · aims to hold the price 8 times in
        10: about 1 in 10 below the bottom, 1 in 10 above the top
      </Typography>

      {t && groupWon != null && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Why this range:</strong> for {groupText(r.group)}, once both ranges were corrected for what they
          missed, {r.method === 'vol' ? "a plain range from the past year's volatility" : "Chronos-2's range"} scored better
          than {r.method === 'vol' ? "Chronos-2's" : 'a plain volatility range'} on {groupWon} of {t.group_dates} test
          dates across all lengths
          {hereWon != null && ` (at ${label(t.horizon)}: ${hereWon} of ${t.comparison_dates}${clear ? ', clearly' : ', not by a clear margin'})`}.
          {r.method === 'vol' && ' Chronos-2 adds nothing here.'}
        </Typography>
      )}

      {t && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>How it tested:</strong> it held the real price <strong>{pct0(t.held)}</strong> of the time ({pct0(t.below)}{' '}
          below the bottom, {pct0(t.above)} above the top) on {t.dates} monthly dates from {t.from?.slice(0, 7)}, each
          time corrected only with what was known by then
          {halves.length === 2 && ` (${halves.map(([h, v]) => `${pct0(v)} in ${h.replace(' → ', '–')}`).join(', ')})`}.
          {other && ` ${otherName}: ${money(other.lower)} – ${money(other.upper)}, held ${pct0(t.other_held)}.`}
        </Typography>
      )}

      {t?.held != null && t.held < FAILED && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          At this length the range failed in testing: it held only {pct0(t.held)}. One bad stretch, such as the 2022 bond
          fall, broke it. Treat it as too narrow.
        </Alert>
      )}

      <PositionSizer price={current} low={r.lower} lowLabel={`the bottom of the ${label(r.horizon)} range`} />

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
        Chronos-2's own 10–90% range, before the correction: {money(r.raw_lower)} – {money(r.raw_upper)}
        {t?.chronos_raw_held != null && ` (held ${pct0(t.chronos_raw_held)} in the test)`}. Neither range says which way the
        price will go; no model here calls that better than "always up".
        {r.relation === 'untested_etf' && ' This ETF was not in the test: the correction measured on all 23 tested ETFs is used.'}
        {r.relation === 'untested_stock' && ' This stock is not in the S&P 500 test: its correction is used, and smaller, wilder stocks may miss more often.'}
        {!TESTED_LABEL[r.horizon] && t && ` ${label(r.horizon)} was not tested: the correction is interpolated and the figures are for ${label(t.horizon)}.`}
      </Typography>
    </Paper>
  );
}

// Tickers to try, from the tested ETF classes
const QUICK: [string, string[]][] = [
  ['Bonds', ['TLT', 'IEF', 'AGG', 'SHY']],
  ['Stock indexes', ['SPY', 'QQQ', 'IWM']],
  ['Commodities', ['GLD', 'SLV']],
];

export function RangeReliability({ onPick }: { onPick: (symbol: string) => void }) {
  const [data, setData] = useState<LikelyRangeReliability | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    apiService.getLikelyRangeReliability().then(setData).catch(() => setFailed(true));
  }, []);
  if (failed || (data && !Object.keys(data.groups).length)) return null;
  const groups = data ? Object.keys(data.groups) : [];
  const horizons = Array.from(new Set(groups.flatMap(g => Object.keys(data!.groups[g].horizons).map(Number))))
    .sort((a, b) => a - b);
  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Typography variant="subtitle2" gutterBottom>
        Likely price range: where it works
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Neither model calls direction or price better than "always up" or "no change", on stocks or on any ETF class
        tested. What does work is a range for how far the price may move, once it is stretched by how much it missed in
        testing. Two ranges were tested: Chronos-2's, and a plain one from the past year's volatility. Each row uses
        the one that scored better for that asset type. Each cell shows how often it held the real price (target 80%),
        tested month by month from 2021 and corrected only with what was known at the time.{' '}
        <strong>Bold</strong>: clearly better than the other range at that length.
      </Typography>
      {data && (
        <Box sx={{ overflowX: 'auto', mb: 1.5 }}>
          <Table size="small" sx={{ '& td, & th': { px: 1, whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow>
                <TableCell>Asset</TableCell>
                <TableCell>Range used</TableCell>
                {horizons.map(h => <TableCell key={h} align="right">{label(h)}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map(g => {
                const row = data.groups[g];
                return (
                  <TableRow key={g}>
                    <TableCell>{groupText(g).replace(/^./, c => c.toUpperCase())}</TableCell>
                    <TableCell>{row.use === 'chronos' ? 'Chronos-2' : 'Volatility'}</TableCell>
                    {horizons.map(h => {
                      const c = row.horizons[String(h)];
                      const held = row.use === 'chronos' ? c?.chronos_held : c?.vol_held;
                      const won = c?.dates != null && c.chronos_better_dates != null
                        ? (row.use === 'chronos' ? c.chronos_better_dates : c.dates - c.chronos_better_dates) : null;
                      const strong = c?.q != null && c.q < CLEAR && won != null && c.dates != null && won > c.dates / 2;
                      return (
                        <TableCell key={h} align="right" sx={{ color: held != null && held < FAILED ? 'warning.main' : undefined }}>
                          {held != null ? (
                            <Tooltip title={`Chronos-2 held ${pct0(c?.chronos_held)}, volatility ${pct0(c?.vol_held)}; the range used scored better on ${won} of ${c?.dates} dates`}>
                              <span style={{ fontWeight: strong ? 700 : 400 }}>{pct0(held)}</span>
                            </Tooltip>
                          ) : '–'}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">Try a bond, index or commodity ETF:</Typography>
        {QUICK.map(([cls, syms]) => syms.map(s => (
          <Chip key={s} label={s} size="small" variant="outlined" onClick={() => onPick(s)} title={`${cls} ETF`} />
        )))}
      </Box>
    </Paper>
  );
}
